import React, { useState, useEffect, useCallback, useRef } from 'react';
import { adsAPI } from '../api';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace('/api', '');

const AdBanner = ({ placement, className = '', maxAds = 5, interval = 5000 }) => {
  const [ads, setAds] = useState([]);
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    adsAPI.getByPlacement(placement)
      .then(res => {
        const a = (res.data.ads || []).slice(0, maxAds);
        setAds(a);
        // Record view for first ad
        if (a.length > 0) adsAPI.recordView(a[0].id).catch(() => {});
      })
      .catch(() => {});
  }, [placement, maxAds]);

  // Auto-slide
  const nextSlide = useCallback(() => {
    if (ads.length <= 1) return;
    setCurrent(prev => {
      const next = (prev + 1) % ads.length;
      // Record view for newly shown ad
      adsAPI.recordView(ads[next].id).catch(() => {});
      return next;
    });
  }, [ads]);

  useEffect(() => {
    if (ads.length <= 1 || paused) return;
    timerRef.current = setInterval(nextSlide, interval);
    return () => clearInterval(timerRef.current);
  }, [ads, paused, interval, nextSlide]);

  if (ads.length === 0) return null;

  const handleClick = (ad) => {
    adsAPI.recordClick(ad.id).catch(() => {});
  };

  const containerSizes = {
    'homepage-top': 'w-full',
    'homepage-mid': 'w-full max-w-4xl mx-auto',
    'services-top': 'w-full',
    'products-top': 'w-full',
    'sidebar': 'w-full',
    'detail-sidebar': 'w-full',
    'desktop-side-left': 'w-full',
    'desktop-side-right': 'w-full',
  };

  const containerClass = containerSizes[placement] || 'w-full';
  const isSidebar = placement.includes('sidebar') || placement.includes('side-');

  return (
    <div
      className={`relative overflow-hidden ${containerClass} ${className}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Slides */}
      <div
        className="flex transition-transform duration-500 ease-in-out"
        style={{ transform: `translateX(-${current * 100}%)` }}
      >
        {ads.map((ad, i) => {
          const imgSrc = ad.image_url?.startsWith('http') ? ad.image_url : `${API_URL}${ad.image_url}`;
          const Wrapper = ad.link_url ? 'a' : 'div';
          const wrapperProps = ad.link_url ? {
            href: ad.link_url,
            target: '_blank',
            rel: 'noopener noreferrer sponsored',
            onClick: () => handleClick(ad),
          } : {};

          return (
            <Wrapper
              key={ad.id}
              {...wrapperProps}
              className="w-full flex-shrink-0 relative block"
            >
              <div className={`overflow-hidden ${isSidebar ? 'rounded-xl' : 'rounded-xl'} bg-gray-50 border border-gray-100`}>
                <img
                  src={imgSrc}
                  alt={ad.title || 'Advertisement'}
                  className={`w-full h-auto object-cover ${isSidebar ? 'max-h-[600px]' : ''}`}
                  loading={i === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                />
              </div>
              <span className="absolute bottom-2 right-2 bg-black/40 text-white/80 text-[8px] px-1.5 py-0.5 rounded font-medium tracking-wider backdrop-blur-sm uppercase">
                Ad
              </span>
            </Wrapper>
          );
        })}
      </div>

      {/* Dots (only if multiple ads) */}
      {ads.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
          {ads.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`transition-all duration-300 rounded-full ${
                i === current
                  ? 'w-5 h-1.5 bg-white shadow-sm'
                  : 'w-1.5 h-1.5 bg-white/50 hover:bg-white/70'
              }`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdBanner;
